import { useRef, useState, useEffect, useCallback, useMemo } from "react";
import { useMathJax } from "../../../hooks/useMathJax";
import { processText, selectionToMarkdown } from "../../../utils/learnUtils";
import { useLearnCtx } from ".././LearnPage";
import {
  getFlatText,
  getRangeStartOffset,
  findBestMatch,
  clearHighlightsFromDOM,
  applyHighlightToDOM,
  makeHighlightRecord,
  captureSelectionWithLatex,
} from "../../../utils/highlightUtils";
import SelectionToolbar from "./SelectionToolbar";

function getFirstTextNode(el) {
  const w = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  return w.nextNode();
}

function getLastTextNode(el) {
  const w = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  let last = null,
    node;
  while ((node = w.nextNode())) last = node;
  return last;
}

/**
 * Renders an EDCOMP_LIST content item.
 * Supports text selection → toolbar → highlight, bookmark, explain, ask AI.
 * @param {{ item: object }} props
 */
export default function ListComp({ item, onExplain, onAskAI }) {
  const { openBookmarkModal, highlights, saveHighlight, deleteHighlight } =
    useLearnCtx();

  const { list_type, text, space } = item.data ?? {};
  const list_items = text ? text.split(";;;split;;;") : [];

  const ref = useRef(null);
  const selectionStartedHere = useRef(false);
  const isClamping = useRef(false);
  const [toolbarPos, setToolbarPos] = useState(null);
  const [copyDone, setCopyDone] = useState(false);

  const capturedSelRef = useRef(null);

  /* ── Highlights for this list only ── */
  const paraHighlights = useMemo(
    () => highlights.filter((h) => h.paragraphId === item._id),
    [highlights, item._id],
  );

  const paraHighlightsRef = useRef(paraHighlights);
  paraHighlightsRef.current = paraHighlights;

  /* ── MathJax + highlight restoration ── */
  const [typesetVersion, setTypesetVersion] = useState(0);
  const onTypesetRef = useRef(null);
  onTypesetRef.current = () => setTypesetVersion((v) => v + 1);

  useMathJax(ref, [text], onTypesetRef);

  const highlightsKey = paraHighlights
    .map((h) => `${h.id}:${h.color}`)
    .join("|");

  useEffect(() => {
    const container = ref.current;
    if (!container || typesetVersion === 0) return;
    clearHighlightsFromDOM(container);
    for (const record of paraHighlights) {
      applyHighlightToDOM(container, record);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typesetVersion, highlightsKey, deleteHighlight]);

  /* ── Add highlight ── */
  const handleHighlight = useCallback(
    (color) => {
      const captured = capturedSelRef.current;
      if (!captured || !ref.current) return;
      if (!captured.text.trim()) return;
      const flat = getFlatText(ref.current);

      let newStart = captured.start;
      let newEnd = captured.end;

      const sorted = paraHighlightsRef.current
        .map((r) => {
          const hStart = findBestMatch(flat, r.exact, r.prefix, r.suffix);
          return hStart === -1
            ? null
            : { record: r, hStart, hEnd: hStart + r.exact.length };
        })
        .filter(Boolean)
        .sort((a, b) => a.hStart - b.hStart);

      for (const { record, hStart, hEnd } of sorted) {
        if (hEnd <= newStart || hStart >= newEnd) continue;
        deleteHighlight(record.id);
        if (record.color === color) {
          newStart = Math.min(newStart, hStart);
          newEnd = Math.max(newEnd, hEnd);
        } else {
          if (hStart < newStart)
            saveHighlight(makeHighlightRecord(record, flat, hStart, newStart));
          if (hEnd > newEnd)
            saveHighlight(makeHighlightRecord(record, flat, newEnd, hEnd));
        }
      }

      saveHighlight({
        paragraphId: item._id,
        color,
        exact: flat.slice(newStart, newEnd),
        prefix: flat.slice(Math.max(0, newStart - 40), newStart),
        suffix: flat.slice(newEnd, newEnd + 40),
      });

      window.getSelection()?.removeAllRanges();
      capturedSelRef.current = null;
      setToolbarPos(null);
    },
    [item._id, saveHighlight, deleteHighlight],
  );

  /* ── Remove highlight ── */
  const handleRemoveHighlight = useCallback(() => {
    const captured = capturedSelRef.current;
    if (!captured || !ref.current) return;
    const { start: selStart, end: selEnd } = captured;
    const flat = getFlatText(ref.current);

    const makeRecord = (base, newStart, newEnd) => ({
      paragraphId: base.paragraphId,
      color: base.color,
      exact: flat.slice(newStart, newEnd),
      prefix: flat.slice(Math.max(0, newStart - 40), newStart),
      suffix: flat.slice(newEnd, newEnd + 40),
    });

    for (const record of paraHighlightsRef.current) {
      const hStart = findBestMatch(
        flat,
        record.exact,
        record.prefix,
        record.suffix,
      );
      if (hStart === -1) continue;
      const hEnd = hStart + record.exact.length;
      if (hEnd <= selStart || hStart >= selEnd) continue;

      deleteHighlight(record.id);

      const clipsStart = selStart <= hStart;
      const clipsEnd = selEnd >= hEnd;
      if (clipsStart && clipsEnd) {
        // whole highlight selected — already deleted
      } else if (clipsStart) {
        saveHighlight(makeRecord(record, selEnd, hEnd));
      } else if (clipsEnd) {
        saveHighlight(makeRecord(record, hStart, selStart));
      } else {
        saveHighlight(makeRecord(record, hStart, selStart));
        saveHighlight(makeRecord(record, selEnd, hEnd));
      }
    }

    window.getSelection()?.removeAllRanges();
    capturedSelRef.current = null;
    setToolbarPos(null);
  }, [deleteHighlight, saveHighlight]);

  /* ── Selection tracking ── */
  const handlePointerDown = useCallback(() => {
    selectionStartedHere.current = true;
  }, []);

  useEffect(() => {
    const reset = () => {
      selectionStartedHere.current = false;
    };
    document.addEventListener("pointerup", reset);
    return () => document.removeEventListener("pointerup", reset);
  }, []);

  useEffect(() => {
    const onSelectionChange = () => {
      if (isClamping.current) return;

      const sel = window.getSelection();
      const container = ref.current;
      if (
        !sel ||
        !sel.anchorNode ||
        !sel.focusNode ||
        sel.rangeCount === 0 ||
        !container
      ) {
        setToolbarPos(null);
        return;
      }

      const anchorInside = container.contains(sel.anchorNode);
      const focusInside = container.contains(sel.focusNode);

      if (selectionStartedHere.current && anchorInside && !focusInside) {
        isClamping.current = true;
        const focusBeforeUs = !!(
          container.compareDocumentPosition(sel.focusNode) &
          Node.DOCUMENT_POSITION_PRECEDING
        );
        if (focusBeforeUs) {
          const first = getFirstTextNode(container);
          if (first)
            sel.setBaseAndExtent(sel.anchorNode, sel.anchorOffset, first, 0);
        } else {
          const last = getLastTextNode(container);
          if (last)
            sel.setBaseAndExtent(
              sel.anchorNode,
              sel.anchorOffset,
              last,
              last.length,
            );
        }
        isClamping.current = false;
        return;
      }

      if (sel.isCollapsed || !anchorInside || !focusInside) {
        capturedSelRef.current = null;
        setToolbarPos(null);
        return;
      }

      const range = sel.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) {
        setToolbarPos(null);
        return;
      }

      const selText = sel.toString();
      const startOff = getRangeStartOffset(range, container);
      capturedSelRef.current =
        startOff !== null
          ? {
              start: startOff,
              end: startOff + selText.length,
              text: selText,
              range: range.cloneRange(),
            }
          : null;

      setToolbarPos({ x: rect.left + rect.width / 2, y: rect.top });
    };

    document.addEventListener("selectionchange", onSelectionChange);
    return () =>
      document.removeEventListener("selectionchange", onSelectionChange);
  }, []);

  useEffect(() => {
    if (!toolbarPos) return;
    const hide = () => setToolbarPos(null);
    window.addEventListener("scroll", hide, { capture: true, passive: true });
    return () => window.removeEventListener("scroll", hide, { capture: true });
  }, [toolbarPos]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const suppress = (e) => e.preventDefault();
    el.addEventListener("contextmenu", suppress);
    return () => el.removeEventListener("contextmenu", suppress);
  }, []);

  /* ── Copy ── */
  const copySelection = useCallback(async (e) => {
    e.stopPropagation();
    const text = selectionToMarkdown();
    try {
      await navigator.clipboard.writeText(text);
      setCopyDone(true);
      setTimeout(() => setCopyDone(false), 1500);
    } catch {
      /* clipboard access denied */
    }
  }, []);

  const preventDeselect = useCallback((e) => e.preventDefault(), []);

  const handleExplain = useCallback(() => {
    const text = capturedSelRef.current?.text;
    if (!text?.trim() || !onExplain) return;
    setToolbarPos(null);
    capturedSelRef.current = null;
    window.getSelection()?.removeAllRanges();
    onExplain(text);
  }, [onExplain]);

  const handleAskAI = useCallback(() => {
    const captured = capturedSelRef.current;
    if (!captured || !onAskAI) return;
    const text =
      ref.current && captured.range
        ? captureSelectionWithLatex(captured.range, ref.current)
        : captured.text;
    if (!text?.trim()) return;
    setToolbarPos(null);
    capturedSelRef.current = null;
    window.getSelection()?.removeAllRanges();
    onAskAI(text);
  }, [onAskAI]);

  const toolbarStyle = toolbarPos
    ? {
        position: "fixed",
        left: `${toolbarPos.x}px`,
        top: `${toolbarPos.y - 8}px`,
        bottom: "auto",
        transform: "translateX(-50%) translateY(-100%)",
        zIndex: 50,
      }
    : null;

  const ListTag = list_type === "itemize" ? "ul" : "ol";
  const listClass =
    list_type === "itemize" ? "edcomp-list-ul" : "edcomp-list-ol";

  return (
    <div
      id={"list:" + String(item._id)}
      ref={ref}
      className="ct__list"
      data-content-id={item._id}
      onPointerDown={handlePointerDown}
    >
      <ListTag className={listClass}>
        {(list_items ?? []).map((itemText, index) => (
          <li key={index} style={space ? { marginBottom: space } : undefined}>
            <span dangerouslySetInnerHTML={{ __html: processText(itemText) }} />
          </li>
        ))}
      </ListTag>
      {toolbarPos && (
        <SelectionToolbar
          style={toolbarStyle}
          onCopy={copySelection}
          copyDone={copyDone}
          onPointerDown={preventDeselect}
          onBookmark={() =>
            openBookmarkModal(item._id, item.title, "list:" + item._id)
          }
          onHighlight={handleHighlight}
          onRemoveHighlight={handleRemoveHighlight}
          onExplain={onExplain ? handleExplain : undefined}
          onAskAI={onAskAI ? handleAskAI : undefined}
        />
      )}
    </div>
  );
}
